import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap, useMapEvents } from 'react-leaflet';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet-geosearch/dist/geosearch.css';
import L from 'leaflet';

interface MapEventsProps {
    onMapClick: (e: any) => void;
    onMapContextMenu?: (e: any) => void;
    polygonDrawPreview?: {
        enabled: boolean;
        points: [number, number][];
        color?: string;
    };
}

export function MapEvents({ onMapClick, onMapContextMenu, polygonDrawPreview }: MapEventsProps) {
    const map = useMap();
    const previewLineRef = useRef<L.Polyline | null>(null);

    const removePreviewLine = useCallback(() => {
        if (!previewLineRef.current) return;
        map.removeLayer(previewLineRef.current);
        previewLineRef.current = null;
    }, [map]);

    const setPreviewLine = useCallback(
        (endLat: number, endLng: number) => {
            if (!polygonDrawPreview?.enabled || polygonDrawPreview.points.length === 0) {
                removePreviewLine();
                return;
            }

            const start = polygonDrawPreview.points[polygonDrawPreview.points.length - 1];
            const positions: [number, number][] = [
                [start[0], start[1]],
                [endLat, endLng],
            ];

            if (!previewLineRef.current) {
                previewLineRef.current = L.polyline(positions, {
                    color: polygonDrawPreview.color ?? 'rgba(8, 40, 110, 0.98)',
                    weight: 5,
                    dashArray: '9, 6',
                    interactive: false,
                }).addTo(map);
                return;
            }

            previewLineRef.current.setLatLngs(positions);
        },
        [map, polygonDrawPreview, removePreviewLine]
    );

    useEffect(() => {
        if (!polygonDrawPreview?.enabled || polygonDrawPreview.points.length === 0) {
            removePreviewLine();
            return;
        }

        const lastPoint = polygonDrawPreview.points[polygonDrawPreview.points.length - 1];
        setPreviewLine(lastPoint[0], lastPoint[1]);
    }, [polygonDrawPreview, removePreviewLine, setPreviewLine]);

    useEffect(() => {
        return () => {
            removePreviewLine();
        };
    }, [removePreviewLine]);

    useMapEvents({
        click: onMapClick,
        contextmenu: (e) => {
            if (onMapContextMenu) {
                onMapContextMenu(e);
            }
        },
        mousemove: (e) => {
            setPreviewLine(e.latlng.lat, e.latlng.lng);
        },
        mouseout: () => {
            removePreviewLine();
        },
    });
    return null;
}

export function SearchControl() {
    const map = useMap();
    const { t } = useTranslation('map');

    useEffect(() => {
        const provider = new OpenStreetMapProvider();

        const searchControl = new (GeoSearchControl as any)({
            provider: provider,
            style: 'bar',
            showMarker: false,
            showPopup: false,
            autoClose: true,
            retainZoomLevel: false,
            animateZoom: true,
            keepResult: true,
            position: 'topleft',
            searchLabel: t('controls.search.label')
        });

        map.addControl(searchControl);

        return () => { map.removeControl(searchControl); }
    }, [map, t]);

    return null;
}
